import { useState, useEffect, useMemo } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import GridLegacy from '@mui/material/GridLegacy'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Formik } from 'formik'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useLocation } from 'react-router'
import toast from 'react-hot-toast'
import * as Yup from 'yup'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import RefreshIcon from '@mui/icons-material/Refresh'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import SubHeader from 'src/components/SubHeader'
import { ordersApi } from 'src/api/ordersApi'
import { organizationApi } from 'src/api/organizationApi'
import { pimApi } from 'src/api/pimApi'
import { useUserProfile } from 'src/contexts/UserProfileContext'
import numeral from 'numeral'

interface ProductItem {
    uid: string
    productId: string
    productName: string
    catalogue?: string
    category?: string
    sku?: string
    quantity: number
    priceType?: 'monthlyMsrp' | 'oneYearMsrp' | 'threeYearMsrp'
    price: number
    total: number
    // Store all available prices for price type switching
    monthlyMsrp?: number
    oneYearMsrp?: number
    threeYearMsrp?: number
}

function CreateOrder() {
    const navigate = useNavigate()
    const location = useLocation()
    const { userData } = useUserProfile()
    const [organizations, setOrganizations] = useState<any[]>([])
    const [filteredProducts, setFilteredProducts] = useState<any[]>([])
    const [productSearchQuery, setProductSearchQuery] = useState('')
    const [productScope, setProductScope] = useState<'pricebook' | 'all'>('all')
    const [pricebookSource, setPricebookSource] = useState<'client' | 'reseller' | null>(null)
    const [pricebookOrganizationId, setPricebookOrganizationId] = useState('')
    const [loading, setLoading] = useState(false)
    const [productsLoading, setProductsLoading] = useState(false)
    const [prefilledOrder, setPrefilledOrder] = useState<any>(null)
    const [orderLoading, setOrderLoading] = useState(() =>
        Boolean(
            location.state?.orderId ||
            location.state?.orderData?._id ||
            location.state?.orderData?.id
        )
    )

    // Edit mode: resolve order id from navigation state, then load full detail via getOrderById
    const navOrderData = location.state?.orderData || null
    const editOrderId =
        location.state?.orderId ||
        navOrderData?._id ||
        navOrderData?.id ||
        null

    const isApproved = prefilledOrder?.status === 'Approved'
    const isBookkeepingRecord = Boolean(
        prefilledOrder?.isBookkeepingRecord ?? navOrderData?.isBookkeepingRecord
    )

    useEffect(() => {
        let cancelled = false

        if (!editOrderId) {
            setPrefilledOrder(null)
            setOrderLoading(false)
            return () => {
                cancelled = true
            }
        }

        const fetchOrder = async () => {
            setOrderLoading(true)
            try {
                const response = await ordersApi.getOrderById(editOrderId)
                const orderData = response?.data?.data || response?.data || null
                if (cancelled) return

                if (!orderData) {
                    toast.error('Failed to load order details')
                    navigate('/orders')
                    return
                }

                setPrefilledOrder(orderData)
            } catch (error: any) {
                if (cancelled) return
                console.error('Error fetching order:', error)
                toast.error(
                    error?.response?.data?.message || 'Failed to load order details'
                )
                navigate('/orders')
            } finally {
                if (!cancelled) {
                    setOrderLoading(false)
                }
            }
        }

        fetchOrder()
        return () => {
            cancelled = true
        }
    }, [editOrderId, navigate])

    useEffect(() => {
        const fetchData = async () => {
            if (!userData?.organization?.id) {
                return
            }
            try {
                const [orgsResponse] = await Promise.all([
                    organizationApi.getByParentId(userData.organization.id, 0, 100)
                ])
                if (orgsResponse?.data) {
                    // Extract organizations array from response.data.data
                    const orgsArray = orgsResponse.data?.data || orgsResponse.data || []
                    // Ensure we only use name and id for the dropdown
                    setOrganizations(orgsArray.map((org: any) => ({
                        id: org.id || org._id,
                        name: org.name
                    })))
                }
                // if (productsResponse?.data) {
                //     setProducts(productsResponse.data)
                //     setFilteredProducts(productsResponse.data)
                // }
            } catch (error) {
                console.error('Error fetching data:', error)
                toast.error('Failed to load organizations or products')
            }
        }
        fetchData()
    }, [userData?.organization?.id])

    // Track selected organization ID for product fetching
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('')

    // Prefer the client's pricebook and fall back to the reseller's pricebook.
    useEffect(() => {
        let cancelled = false

        const resolvePricebookSource = async () => {
            if (productScope !== 'pricebook' || !selectedOrganizationId) {
                setPricebookSource(null)
                setPricebookOrganizationId('')
                return
            }

            const resellerOrganizationId = userData?.organization?.id || ''

            try {
                const response = await pimApi.getPricebooks({
                    page: 0,
                    pageSize: 1,
                    organizationId: selectedOrganizationId,
                    search: '',
                })
                if (cancelled) return

                const clientProducts = response?.data?.data || response?.data || []
                if (Array.isArray(clientProducts) && clientProducts.length > 0) {
                    setPricebookSource('client')
                    setPricebookOrganizationId(selectedOrganizationId)
                } else if (resellerOrganizationId) {
                    setPricebookSource('reseller')
                    setPricebookOrganizationId(resellerOrganizationId)
                }
            } catch (error) {
                console.error('Error resolving pricebook source:', error)
                if (!cancelled && resellerOrganizationId) {
                    setPricebookSource('reseller')
                    setPricebookOrganizationId(resellerOrganizationId)
                }
            }
        }

        resolvePricebookSource()
        return () => {
            cancelled = true
        }
    }, [productScope, selectedOrganizationId, userData?.organization?.id])

    // Fetch products when organization, search, or product scope changes.
    useEffect(() => {
        const fetchProducts = async () => {
            if (!selectedOrganizationId) {
                setFilteredProducts([])
                return
            }

            if (productScope === 'pricebook' && !pricebookOrganizationId) {
                setFilteredProducts([])
                return
            }

            setProductsLoading(true)
            try {
                if (productScope === 'all') {
                    const response = await pimApi.getAllProducts({
                        page: 0,
                        pageSize: 20,
                        search: productSearchQuery,
                        isActive: true,
                    })
                    const products = response?.data?.data || response?.data || []
                    setFilteredProducts(Array.isArray(products) ? products : [])
                } else {
                    const response = await pimApi.getPricebooks({
                        page: 0,
                        pageSize: 25,
                        organizationId: pricebookOrganizationId,
                        search: productSearchQuery,
                    })
                    const pricebookProducts = response?.data?.data || response?.data || []
                    const products = (Array.isArray(pricebookProducts) ? pricebookProducts : [])
                        .map((item: any) => {
                            const product = item?.product || {}
                            const productId = product?._id || product?.id
                            return {
                                ...product,
                                _id: productId,
                                id: productId,
                                name: product?.Name || product?.name || '',
                                sku: product?.SKU || product?.sku || '',
                                productTemplateObject: {
                                    ...product,
                                    Name: product?.Name || product?.name || '',
                                    SKU: product?.SKU || product?.sku || '',
                                },
                                productCatalogues: item?.productCatalogues,
                                productCategory: item?.productCategories || item?.productCategory,
                            }
                        })
                        .filter((product: any) => product?._id)
                    setFilteredProducts(products)
                }
            } catch (error) {
                console.error('Error fetching products:', error)
                setFilteredProducts([])
            } finally {
                setProductsLoading(false)
            }
        }

        // Debounce search query
        const timeoutId = setTimeout(() => {
            fetchProducts()
        }, 500)

        return () => clearTimeout(timeoutId)
    }, [productSearchQuery, selectedOrganizationId, productScope, pricebookOrganizationId])

    const validationSchema = Yup.object({
        orderNumber: Yup.string()
            .required('Order number is required')
            .min(3, 'Order number must be at least 3 characters')
            .max(128, 'Order number cannot exceed 128 characters'),
        organization: Yup.string().required('Organization is required'),
        orderDate: Yup.date()
            .nullable()
            .required('Order date is required'),
        vat: Yup.number()
            .required('VAT is required')
            .transform((value, originalValue) => {
                // Handle string values like "21.0" or "0.0"
                if (typeof originalValue === 'string') {
                    const parsed = parseFloat(originalValue);
                    return Number.isNaN(parsed) ? originalValue : parsed;
                }
                return value;
            })
            .oneOf([0, 21, 0.0, 21.0], 'VAT must be either 0 or 21')
            .test('is-valid-vat', 'VAT must be either 0 or 21', (value) => {
                const numValue = typeof value === 'string' ? parseFloat(value) : value;
                return numValue === 0 || numValue === 21;
            }),
        products: Yup.array()
            .of(
                Yup.object({
                    productId: Yup.string().required('Product is required'),
                    quantity: Yup.number()
                        .required('Quantity is required')
                        .min(1, 'Quantity must be at least 1')
                        .max(9999, 'Quantity cannot exceed 9999')
                        .integer('Quantity must be a whole number'),
                })
            )
            .min(1, 'At least one product is required'),
        notes: Yup.string()
            .max(500, 'Notes cannot exceed 500 characters'),
    })

    const handleSubmit = async (values: any, { setSubmitting, resetForm }: any) => {
        try {
            setLoading(true)
            // Format date as YYYY-MM-DD
            const formatDate = (date: Date | null) => {
                if (!date) return ''
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                return `${year}-${month}-${day}`
            }

            // Ensure VAT is a number (handle "21.0" or "0.0" from API)
            const vatRate = typeof values.vat === 'string'
                ? parseFloat(values.vat)
                : (values.vat || 0);

            const payload: {
                orderNumber: string;
                fromOrg: string;
                toOrg: string;
                orderDate: string;
                vat: number;
                note?: string;
                products: Array<{
                    productId: string;
                    quantity: number;
                    priceType: 'monthlyMsrp' | 'oneYearMsrp' | 'threeYearMsrp';
                }>;
            } = {
                orderNumber: values.orderNumber.trim(),
                fromOrg: userData?.organization?.id || '', // Current user's organization
                toOrg: values.organization, // Selected organization from dropdown
                orderDate: formatDate(values.orderDate), // Keep as YYYY-MM-DD format
                vat: vatRate,
                note: values.notes || '',
                products: values.products.map((p: ProductItem) => ({
                    productId: p.productId,
                    quantity: p.quantity,
                    priceType: p.priceType,
                })),
            }

            // Check if we're editing an existing order or creating a new one
            const isEditMode = prefilledOrder && (prefilledOrder._id || prefilledOrder.id)
            let response

            if (isEditMode) {
                // Update existing order using PATCH
                const orderId = prefilledOrder._id || prefilledOrder.id
                response = await ordersApi.updateOrder(orderId, payload as any)
            } else {
                // Create new order using POST
                response = await ordersApi.createOrder(payload as any)
            }

            if (response?.status === 200 || response?.status === 201) {
                toast.success(isEditMode ? 'Order updated successfully' : 'Order created successfully')
                resetForm()
                navigate('/orders')
            } else {
                toast.error(isEditMode ? 'Failed to update order' : 'Failed to create order')
            }
        } catch (error: any) {
            console.error('Error saving order:', error)
            const isEditMode = prefilledOrder && (prefilledOrder._id || prefilledOrder.id)
            const errorMessage =
                error?.response?.data?.message || (isEditMode ? 'Failed to update order' : 'Failed to create order')
            toast.error(errorMessage)
        } finally {
            setLoading(false)
            setSubmitting(false)
        }
    }

    const generateOrderNumber = () => {
        const timestamp = Date.now()
        const random = Math.floor(Math.random() * 1000)
        return `ORD-${timestamp}-${random}`
    }

    // Convert prefilled order data to form initial values
    const getInitialValues = () => {
        if (prefilledOrder) {
            const products: ProductItem[] = (prefilledOrder.products || []).map((p: any) => {
                // Get current price and price type
                const currentPrice = p.price || p.unitPrice || 0
                const currentPriceType = p.priceType || 'monthlyMsrp'

                // Reconstruct price fields based on the current price and priceType
                // If individual price fields exist, use them; otherwise, map current price to its type
                let monthlyMsrp = p.monthlyMsrp || 0
                let oneYearMsrp = p.oneYearMsrp || 0
                let threeYearMsrp = p.threeYearMsrp || 0

                // If we don't have separate price fields, assign the current price to its type
                const needsReconstruction = !monthlyMsrp && !oneYearMsrp && !threeYearMsrp && currentPrice > 0
                if (needsReconstruction) {
                    if (currentPriceType === 'monthlyMsrp') monthlyMsrp = currentPrice
                    if (currentPriceType === 'oneYearMsrp') oneYearMsrp = currentPrice
                    if (currentPriceType === 'threeYearMsrp') threeYearMsrp = currentPrice
                }

                // Determine valid price type based on available prices
                let priceType: 'monthlyMsrp' | 'oneYearMsrp' | 'threeYearMsrp' = 'monthlyMsrp'
                if (currentPriceType === 'monthlyMsrp' && monthlyMsrp > 0) {
                    priceType = 'monthlyMsrp'
                } else if (currentPriceType === 'oneYearMsrp' && oneYearMsrp > 0) {
                    priceType = 'oneYearMsrp'
                } else if (currentPriceType === 'threeYearMsrp' && threeYearMsrp > 0) {
                    priceType = 'threeYearMsrp'
                } else if (monthlyMsrp > 0) {
                    // Fallback to first available price type
                    priceType = 'monthlyMsrp'
                } else if (oneYearMsrp > 0) {
                    priceType = 'oneYearMsrp'
                } else if (threeYearMsrp > 0) {
                    priceType = 'threeYearMsrp'
                }

                return {
                    uid: crypto.randomUUID(),
                    productId: p.productId || p._id || p.id || p.packageId,
                    productName: p.name || p.productName || p.packageName || '',
                    catalogue: p.catalogueName || p.catalogue || '',
                    category: p.categoryName || p.category || '',
                    sku: p.sku || '',
                    quantity: p.quantity || 1,
                    priceType,
                    price: currentPrice,
                    total: p.total || ((p.quantity || 1) * currentPrice),
                    monthlyMsrp,
                    oneYearMsrp,
                    threeYearMsrp,
                }
            })

            const orderDate = prefilledOrder.orderDate
                ? new Date(prefilledOrder.orderDate)
                : null

            // Normalize VAT to number (0 or 21)
            let vatValue = 0
            if (prefilledOrder.vat) {
                const parsedVat = typeof prefilledOrder.vat === 'string'
                    ? parseFloat(prefilledOrder.vat)
                    : prefilledOrder.vat
                vatValue = parsedVat === 21 || parsedVat === 21.0 ? 21 : 0
            }

            // Extract organization ID - handle both object and string cases
            let organizationId = ''
            if (prefilledOrder.toOrganization && typeof prefilledOrder.toOrganization === 'object') {
                organizationId = prefilledOrder.toOrganization.id || prefilledOrder.toOrganization._id || ''
            } else if (typeof prefilledOrder.toOrganization === 'string') {
                organizationId = prefilledOrder.toOrganization
            } else if (prefilledOrder.toOrg) {
                organizationId = prefilledOrder.toOrg
            }

            return {
                orderNumber: prefilledOrder.orderNumber || '',
                organization: organizationId,
                orderDate,
                vat: vatValue,
                products,
                notes: prefilledOrder.note || '',
            }
        }

        return {
            orderNumber: '',
            organization: '',
            orderDate: new Date(),
            vat: 0,
            products: [],
            notes: '',
        }
    }

    // Set the selected organization ID when pre-filled data is available
    useEffect(() => {
        if (prefilledOrder) {
            let orgId = ''

            // Check if toOrganization is an object with id
            if (prefilledOrder.toOrganization && typeof prefilledOrder.toOrganization === 'object') {
                orgId = prefilledOrder.toOrganization.id || prefilledOrder.toOrganization._id || ''
            }
            // Check if toOrganization is just a string ID
            else if (typeof prefilledOrder.toOrganization === 'string') {
                orgId = prefilledOrder.toOrganization
            }
            // Check if toOrg exists
            else if (prefilledOrder.toOrg) {
                orgId = prefilledOrder.toOrg
            }

            if (orgId) {
                setSelectedOrganizationId(orgId)
            }
        }
    }, [prefilledOrder, organizations])

    // Memoize initial values to prevent unnecessary resets
    const initialValues = useMemo(() => getInitialValues(), [prefilledOrder])

    return (
        <>
            <Helmet>
                <title>IT22 ERP | {editOrderId || prefilledOrder ? 'Edit Order' : 'Create Order'}</title>
            </Helmet>
            <SubHeader title={editOrderId || prefilledOrder ? "Edit Order" : "Create Order"} />
            <Box px="30px" py="20px">
                {orderLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                {isBookkeepingRecord && (
                    <Box
                        mb={2}
                        px={2}
                        py={1.5}
                        borderRadius="4px"
                        bgcolor="#FFF8E1"
                        display="flex"
                        alignItems="center"
                        style={{ gap: 10 }}
                    >
                        <WarningAmberOutlinedIcon style={{ color: '#ED6C02', fontSize: 20, flexShrink: 0 }} />
                        <Typography
                            variant="body2"
                            style={{ color: '#663C00', fontWeight: 500, lineHeight: 1.4 }}
                        >
                            This order is for bookkeeping purposes only. It should not be used to generate invoices or for any other purpose.
                        </Typography>
                    </Box>
                )}
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleBlur,
                        handleChange,
                        handleSubmit: formikHandleSubmit,
                        setFieldValue,
                        isSubmitting,
                    }) => {
                        // Calculate totals
                        const subTotal = values.products.reduce(
                            (sum, p) => sum + (p.total || 0),
                            0
                        )
                        // Ensure VAT is a number for calculation (handle "21.0" or "0.0" from API)
                        const vatForCalc = typeof values.vat === 'string'
                            ? parseFloat(values.vat)
                            : (values.vat || 0);
                        const vatAmount = (subTotal * vatForCalc) / 100
                        const total = subTotal + vatAmount

                        const updateProductQuantity = (uid: string, quantity: number) => {
                            const updatedProducts = values.products.map((p) => {
                                if (p.uid === uid) {
                                    const newTotal = (p.price || 0) * quantity
                                    return { ...p, quantity, total: newTotal }
                                }
                                return p
                            })
                            setFieldValue('products', updatedProducts)
                        }

                        const addProduct = async (product: any) => {
                            // Extract product ID (can be _id or id)
                            const productId = product._id || product.id
                            // Check if product already exists
                            const exists = values.products.find((p) => p.productId === productId)
                            if (exists) {
                                // If exists, just increase quantity
                                updateProductQuantity(exists.uid, exists.quantity + 1)
                                setProductSearchQuery('')
                                return
                            }

                            const fromOrganizationId = userData?.organization?.id || ''
                            const toOrganizationId = selectedOrganizationId || values.organization
                            if (!fromOrganizationId || !toOrganizationId || !productId) {
                                toast.error('Please select organization and product')
                                return
                            }

                            try {
                                const priceResponse = await ordersApi.getProductPriceAgainstFromAndToOrg(
                                    fromOrganizationId,
                                    toOrganizationId,
                                    productId
                                )
                                const priceData =
                                    priceResponse?.data?.data ||
                                    priceResponse?.data ||
                                    {}
                                const template =
                                    priceData?.productTemplateObject ||
                                    product.productTemplateObject ||
                                    {}

                                const toPrice = (value: unknown): number => {
                                    if (value === null || value === undefined || value === '') return 0
                                    const parsed = Number(value)
                                    return Number.isFinite(parsed) ? parsed : 0
                                }

                                const monthlyPrice = toPrice(template['Monthly MSRP'])
                                const oneYearPrice = toPrice(template['1-Year MSRP (Revenue per year)'])
                                const threeYearPrice = toPrice(template['3-Year MSRP'])

                                let priceType: 'monthlyMsrp' | 'oneYearMsrp' | 'threeYearMsrp' = 'monthlyMsrp'
                                let price = 0

                                if (monthlyPrice > 0) {
                                    priceType = 'monthlyMsrp'
                                    price = monthlyPrice
                                } else if (oneYearPrice > 0) {
                                    priceType = 'oneYearMsrp'
                                    price = oneYearPrice
                                } else if (threeYearPrice > 0) {
                                    priceType = 'threeYearMsrp'
                                    price = threeYearPrice
                                }

                                const newProduct: ProductItem = {
                                    uid: crypto.randomUUID(),
                                    productId,
                                    productName: template.Name || product.name || '',
                                    catalogue: product.productCatalogues?.name || product.catalogue || '',
                                    category: product.productCategory?.name || product.category || '',
                                    sku: template.SKU || product.sku || productId,
                                    quantity: 1,
                                    priceType,
                                    price,
                                    total: price,
                                    monthlyMsrp: monthlyPrice,
                                    oneYearMsrp: oneYearPrice,
                                    threeYearMsrp: threeYearPrice,
                                }
                                setFieldValue('products', [...values.products, newProduct])
                            } catch (error) {
                                console.error('Error fetching product price:', error)
                                toast.error('Failed to fetch product price')
                            }
                            setProductSearchQuery('')
                        }

                        const updateProductPriceType = (uid: string, newPriceType: 'monthlyMsrp' | 'oneYearMsrp' | 'threeYearMsrp') => {
                            const updatedProducts = values.products.map((p) => {
                                if (p.uid === uid) {
                                    // Get the price based on the new price type
                                    let newPrice = 0
                                    if (newPriceType === 'monthlyMsrp' && p.monthlyMsrp) {
                                        newPrice = p.monthlyMsrp
                                    } else if (newPriceType === 'oneYearMsrp' && p.oneYearMsrp) {
                                        newPrice = p.oneYearMsrp
                                    } else if (newPriceType === 'threeYearMsrp' && p.threeYearMsrp) {
                                        newPrice = p.threeYearMsrp
                                    }
                                    const newTotal = newPrice * p.quantity
                                    return { ...p, priceType: newPriceType, price: newPrice, total: newTotal }
                                }
                                return p
                            })
                            setFieldValue('products', updatedProducts)
                        }

                        const removeProduct = (uid: string) => {
                            const updatedProducts = values.products.filter((p) => p.uid !== uid)
                            setFieldValue('products', updatedProducts)
                        }

                        return (
                            <form onSubmit={formikHandleSubmit}>
                                <GridLegacy container spacing={3}>
                                    {/* Order Details Row */}
                                    <GridLegacy item xs={12}>
                                        <GridLegacy container spacing={2}>
                                            <GridLegacy item xs={12} md={3}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Order #"
                                                    name="orderNumber"
                                                    value={values.orderNumber}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={Boolean(touched.orderNumber && errors.orderNumber)}
                                                    helperText={
                                                        touched.orderNumber && errors.orderNumber
                                                    }
                                                    required
                                                    variant="outlined"
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setFieldValue('orderNumber', generateOrderNumber())
                                                                    }}
                                                                    disabled={isApproved}
                                                                >
                                                                    <RefreshIcon fontSize="small" />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    disabled={isApproved}
                                                />
                                            </GridLegacy>

                                            <GridLegacy item xs={12} md={3}>
                                                <Autocomplete
                                                    options={organizations}
                                                    disabled={Boolean(prefilledOrder) || isApproved}
                                                    getOptionLabel={(option) => option?.name || ''}
                                                    value={
                                                        values.organization
                                                            ? organizations.find(
                                                                (org) => org.id === values.organization
                                                            ) || null
                                                            : null
                                                    }
                                                    onChange={(_, newValue) => {
                                                        const orgId = newValue?.id || ''
                                                        setFieldValue('organization', orgId)
                                                        setSelectedOrganizationId(orgId)
                                                        // Clear products when organization changes
                                                        setFilteredProducts([])
                                                        setProductSearchQuery('')
                                                        setPricebookSource(null)
                                                        setPricebookOrganizationId('')
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            size="small"
                                                            label="To Organization"
                                                            error={Boolean(
                                                                touched.organization && errors.organization
                                                            )}
                                                            helperText={
                                                                touched.organization && errors.organization
                                                            }
                                                            required
                                                            variant="outlined"
                                                        />
                                                    )}
                                                />
                                            </GridLegacy>



                                            {/* <GridLegacy container spacing={2} style={{ marginTop: '8px' }}> */}
                                            <GridLegacy item xs={12} md={3}>
                                                <DatePicker
                                                    selected={values.orderDate}
                                                    disabled={isApproved}
                                                    onChange={(date: Date | null) => {
                                                        if (date) {
                                                            const adjustedDate = new Date(date)
                                                            adjustedDate.setHours(12, 0, 0, 0)
                                                            setFieldValue('orderDate', adjustedDate)
                                                        } else {
                                                            setFieldValue('orderDate', null)
                                                        }
                                                    }}
                                                    dateFormat="dd/MM/yyyy"
                                                    placeholderText="Select date"
                                                    isClearable
                                                    customInput={
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Date"
                                                            error={Boolean(
                                                                touched.orderDate && errors.orderDate
                                                            )}
                                                            helperText={
                                                                touched.orderDate && errors.orderDate
                                                            }
                                                            required
                                                            variant="outlined"
                                                            InputProps={{
                                                                [values.orderDate ? 'startAdornment' : 'endAdornment']: (
                                                                    <InputAdornment position={values.orderDate ? 'start' : 'end'}>
                                                                        <CalendarTodayIcon fontSize="small" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    }
                                                />
                                            </GridLegacy>

                                            <GridLegacy item xs={12} md={3}>
                                                <FormControl
                                                    fullWidth
                                                    size="small"
                                                    error={Boolean(touched.vat && errors.vat)}
                                                    variant="outlined"
                                                    disabled={isApproved}
                                                >
                                                    <InputLabel>VAT % *</InputLabel>
                                                    <Select
                                                        value={(() => {
                                                            const vatVal = typeof values.vat === 'string'
                                                                ? parseFloat(values.vat)
                                                                : (values.vat ?? 0);
                                                            // Ensure value is either 0 or 21
                                                            return vatVal === 21 ? 21 : 0;
                                                        })()}
                                                        onChange={(e) => {
                                                            const vatValue = typeof e.target.value === 'string'
                                                                ? parseFloat(e.target.value)
                                                                : e.target.value;
                                                            setFieldValue('vat', vatValue);
                                                        }}
                                                        onBlur={handleBlur}
                                                        name="vat"
                                                        label="VAT % *"
                                                    >
                                                        <MenuItem value={0}>0%</MenuItem>
                                                        <MenuItem value={21}>21%</MenuItem>
                                                    </Select>
                                                    {touched.vat && errors.vat && (
                                                        <FormHelperText>{errors.vat}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            </GridLegacy>
                                        </GridLegacy>
                                    </GridLegacy>

                                    {/* Products Section */}
                                    <GridLegacy item xs={12}>
                                        <Paper elevation={1} style={{ padding: '20px' }}>
                                            <Typography variant="h6" gutterBottom>
                                                Products ({values.products.length})
                                            </Typography>

                                            {values.products.length === 0 ? (
                                                <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    minHeight="200px"
                                                >
                                                    <Typography color="textSecondary">
                                                        No Product Added
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box sx={{ overflowX: 'auto', mt: 2 }}>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell style={{ padding: '8px 16px' }}>#</TableCell>
                                                                <TableCell style={{ padding: '8px 16px' }}>CATALOGUE</TableCell>
                                                                <TableCell style={{ padding: '8px 16px' }}>CATEGORY</TableCell>
                                                                <TableCell style={{ padding: '8px 16px' }}>SKU</TableCell>
                                                                <TableCell style={{ padding: '8px 16px' }}>NAME</TableCell>
                                                                <TableCell style={{ padding: '8px 16px' }}>QUANTITY</TableCell>
                                                                <TableCell style={{ padding: '8px 16px' }}>PRICE TYPE</TableCell>
                                                                <TableCell style={{ padding: '8px 16px' }}>PRICE</TableCell>
                                                                <TableCell style={{ padding: '8px 16px' }}>TOTAL</TableCell>
                                                                <TableCell style={{ padding: '8px 16px' }}>ACTION</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {values.products.map((product, index) => (
                                                                <TableRow key={product.uid}>
                                                                    <TableCell style={{ padding: '8px 16px' }}>{index + 1}</TableCell>
                                                                    <TableCell style={{ padding: '8px 16px' }}>{product.catalogue || '-'}</TableCell>
                                                                    <TableCell style={{ padding: '8px 16px' }}>{product.category || '-'}</TableCell>
                                                                    <TableCell style={{ padding: '8px 16px' }}>{product.sku || '-'}</TableCell>
                                                                    <TableCell style={{ padding: '8px 16px', maxWidth: '200px' }}>{product.productName}</TableCell>
                                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                                        <TextField
                                                                            type="number"
                                                                            value={product.quantity}
                                                                            onChange={(e) => {
                                                                                let qty = parseInt(e.target.value, 10) || 1
                                                                                if (qty > 9999) qty = 9999
                                                                                if (qty < 1) qty = 1
                                                                                updateProductQuantity(product.uid, qty)
                                                                            }}
                                                                            inputProps={{ min: 1, max: 9999, style: { width: '60px' } }}
                                                                            size="small"
                                                                            disabled={isApproved}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                                        {prefilledOrder ? (
                                                                            // In edit mode, show price type as read-only text
                                                                            <Typography variant="body2">
                                                                                {product.priceType === 'monthlyMsrp' && 'Monthly MSRP'}
                                                                                {product.priceType === 'oneYearMsrp' && '1-Year MSRP'}
                                                                                {product.priceType === 'threeYearMsrp' && '3-Year MSRP'}
                                                                            </Typography>
                                                                        ) : (
                                                                            // In create mode, allow price type selection
                                                                            <FormControl size="small" variant="outlined" style={{ minWidth: 120 }} disabled={isApproved}>
                                                                                <Select
                                                                                    value={(() => {
                                                                                        // Ensure the value matches an available option
                                                                                        const hasMonthly = product.monthlyMsrp && product.monthlyMsrp > 0
                                                                                        const hasOneYear = product.oneYearMsrp && product.oneYearMsrp > 0
                                                                                        const hasThreeYear = product.threeYearMsrp && product.threeYearMsrp > 0

                                                                                        if (product.priceType === 'monthlyMsrp' && hasMonthly) return 'monthlyMsrp'
                                                                                        if (product.priceType === 'oneYearMsrp' && hasOneYear) return 'oneYearMsrp'
                                                                                        if (product.priceType === 'threeYearMsrp' && hasThreeYear) return 'threeYearMsrp'

                                                                                        // Fallback to first available option
                                                                                        if (hasMonthly) return 'monthlyMsrp'
                                                                                        if (hasOneYear) return 'oneYearMsrp'
                                                                                        if (hasThreeYear) return 'threeYearMsrp'

                                                                                        return 'monthlyMsrp' // Final fallback
                                                                                    })()}
                                                                                    onChange={(e) => {
                                                                                        const newPriceType = e.target.value as 'monthlyMsrp' | 'oneYearMsrp' | 'threeYearMsrp'
                                                                                        updateProductPriceType(product.uid, newPriceType)
                                                                                    }}
                                                                                    displayEmpty
                                                                                >
                                                                                    {product.monthlyMsrp && product.monthlyMsrp > 0 && (
                                                                                        <MenuItem value="monthlyMsrp">Monthly MSRP</MenuItem>
                                                                                    )}
                                                                                    {product.oneYearMsrp && product.oneYearMsrp > 0 && (
                                                                                        <MenuItem value="oneYearMsrp">1-Year MSRP</MenuItem>
                                                                                    )}
                                                                                    {product.threeYearMsrp && product.threeYearMsrp > 0 && (
                                                                                        <MenuItem value="threeYearMsrp">3-Year MSRP</MenuItem>
                                                                                    )}
                                                                                    {(!product.monthlyMsrp || product.monthlyMsrp === 0) &&
                                                                                        (!product.oneYearMsrp || product.oneYearMsrp === 0) &&
                                                                                        (!product.threeYearMsrp || product.threeYearMsrp === 0) && (
                                                                                            <MenuItem value="monthlyMsrp" disabled>
                                                                                                No prices available
                                                                                            </MenuItem>
                                                                                        )}
                                                                                </Select>
                                                                            </FormControl>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                                        {numeral(product.price || 0).format('€0,0.00')}
                                                                    </TableCell>
                                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                                        {numeral(product.total || 0).format('€0,0.00')}
                                                                    </TableCell>
                                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                                        <IconButton
                                                                            size="small"
                                                                            color="secondary"
                                                                            onClick={() => removeProduct(product.uid)}
                                                                            disabled={isApproved}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </Box>
                                            )}
                                            <Box style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
                                                <Box mt={3} style={{ width: '50%' }}>
                                                    <Box style={{ marginBottom: '10px' }}>
                                                        <RadioGroup
                                                            row
                                                            value={productScope}
                                                            onChange={(event) => {
                                                                setProductScope(event.target.value as 'pricebook' | 'all')
                                                                setProductSearchQuery('')
                                                                setFilteredProducts([])
                                                                setPricebookSource(null)
                                                                setPricebookOrganizationId('')
                                                            }}
                                                        >
                                                            <FormControlLabel
                                                                value="pricebook"
                                                                disabled={!selectedOrganizationId || isApproved}
                                                                control={
                                                                    <Radio
                                                                        color="primary"
                                                                        size="small"
                                                                        icon={<RadioButtonUncheckedIcon fontSize="small" />}
                                                                        checkedIcon={<CheckCircleIcon fontSize="small" />}
                                                                    />
                                                                }
                                                                label="Pricebook products"
                                                            />
                                                            <FormControlLabel
                                                                value="all"
                                                                disabled={!selectedOrganizationId || isApproved}
                                                                control={
                                                                    <Radio
                                                                        color="primary"
                                                                        size="small"
                                                                        icon={<RadioButtonUncheckedIcon fontSize="small" />}
                                                                        checkedIcon={<CheckCircleIcon fontSize="small" />}
                                                                    />
                                                                }
                                                                label="All products"
                                                            />
                                                        </RadioGroup>

                                                        {productScope === 'pricebook' && pricebookSource && (
                                                            <Typography
                                                                variant="body2"
                                                                color="textSecondary"
                                                                style={{ marginBottom: 8 }}
                                                            >
                                                                {pricebookSource === 'client'
                                                                    ? 'Showing client pricebook products'
                                                                    : 'Showing reseller pricebook products'}
                                                            </Typography>
                                                        )}

                                                        <Autocomplete
                                                            options={filteredProducts}
                                                            loading={productsLoading}
                                                            disabled={!selectedOrganizationId || isApproved}
                                                            value={null}
                                                            inputValue={productSearchQuery}
                                                            filterOptions={(options) => options}
                                                            getOptionLabel={(option) => {
                                                                const template = option?.productTemplateObject || {}
                                                                const productName = template.Name || option?.name || ''
                                                                const sku = template.SKU || option?.sku || ''
                                                                return sku ? `${productName} (${sku})` : productName
                                                            }}
                                                            onInputChange={(_, newInputValue, reason) => {
                                                                if (reason === 'reset') return
                                                                setProductSearchQuery(newInputValue)
                                                            }}
                                                            onChange={(_, newValue) => {
                                                                if (newValue) {
                                                                    addProduct(newValue)
                                                                    setProductSearchQuery('')
                                                                }
                                                            }}
                                                            noOptionsText={
                                                                !selectedOrganizationId
                                                                    ? 'Please select an organization first'
                                                                    : productSearchQuery
                                                                        ? 'No products found'
                                                                        : 'Type to search products'
                                                            }
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    label="Product"
                                                                    placeholder={
                                                                        !selectedOrganizationId
                                                                            ? 'Please select an organization first to view products'
                                                                            : 'Search product name or SKU'
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </Box>

                                                    <TextField
                                                        fullWidth
                                                        name="notes"
                                                        placeholder="Notes and Disclaimer"
                                                        value={values.notes}
                                                        onChange={(e) => {
                                                            const newValue = e.target.value;
                                                            if (newValue.length <= 500) {
                                                                setFieldValue('notes', newValue);
                                                            }
                                                        }}
                                                        onBlur={handleBlur}
                                                        variant="outlined"
                                                        size="small"
                                                        multiline
                                                        rows={4}
                                                        error={Boolean(touched.notes && errors.notes)}
                                                        disabled={isApproved}
                                                        helperText={
                                                            (touched.notes && errors.notes) ||
                                                            `${values.notes?.length || 0}/500 characters`
                                                        }
                                                    />
                                                </Box>
                                                <Box mt={3} position="relative" style={{ width: '30%', }}>
                                                    <Card >
                                                        <CardContent>
                                                            <Box display="flex" justifyContent="space-between" mb={1}>
                                                                <Typography variant="body2">Sub Total</Typography>
                                                                <Typography variant="body2">
                                                                    {numeral(subTotal).format('€0,0.00')}
                                                                </Typography>
                                                            </Box>
                                                            <Box display="flex" justifyContent="space-between" mb={1}>
                                                                <Typography variant="body2">VAT</Typography>
                                                                <Typography variant="body2">
                                                                    {numeral(vatAmount).format('€0,0.00')}
                                                                </Typography>
                                                            </Box>
                                                            <Box display="flex" justifyContent="space-between" mb={2}>
                                                                <Typography variant="h6">Total</Typography>
                                                                <Typography variant="h6">
                                                                    {numeral(total).format('€0,0.00')}
                                                                </Typography>
                                                            </Box>
                                                            <Box display="flex" justifyContent="flex-end" gap="10px" mt={3}>
                                                                <Button
                                                                    variant="outlined"
                                                                    onClick={() => navigate('/orders')}
                                                                    disabled={isSubmitting || loading}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                                <Button
                                                                    type="submit"
                                                                    variant="contained"
                                                                    color="primary"
                                                                    disabled={isSubmitting || loading || isApproved}
                                                                >
                                                                    {loading
                                                                        ? (prefilledOrder ? 'Updating...' : 'Creating...')
                                                                        : (prefilledOrder ? 'Update Order' : 'Create Order')
                                                                    }
                                                                </Button>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Box>
                                            </Box>


                                        </Paper>

                                    </GridLegacy>

                                    {/* Financial Summary and Actions */}

                                </GridLegacy>
                            </form>
                        )
                    }}
                </Formik>
                    </>
                )}
            </Box>
        </>
    )
}

export default CreateOrder
